import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { AnalysisApiService } from 'src/app/data-transfer/services/analysis-api-service';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { AnalysisNotPossibleDialogComponent } from 'src/app/components/dialogs/analysis-not-possible-dialog/analysis-not-possible-dialog.component';

// ============================================================================
// Types
// ============================================================================

export type Rating = 'UNKNOWN' | 'GREEN' | 'YELLOW' | 'RED';

export interface DisclaimerTranslationDto {
  culture: string;
  translatedName: string;
  translatedDescription: string;
}
export interface GmsDisclaimer {
  id: string;
  name: string;
  description: string;
  translations: DisclaimerTranslationDto[];
}
export interface GmsRating {
  rating: Rating;
  percentage: number;
  disclaimers: GmsDisclaimer[];
}
export interface GmsLayerResult {
  id: string;
  analyzedObject: string;
  ratings: GmsRating[];
}
export interface GmsComponentResult {
  id: string;
  analyzedObject: string;
  disclaimers: GmsDisclaimer[];
  results: GmsLayerResult[];
}
export interface GmsSortingFractionResult {
  id: string;
  sortingFraction: string;
  disclaimers: GmsDisclaimer[];
  results: GmsComponentResult[];
}
export type GmsLogLevel = 'Trace' | 'Debug' | 'Information' | 'Warning' | 'Error';
export interface GmsLogMessage {
  content: string;
  logLevel: GmsLogLevel;
}
export interface GmsResult {
  id: string;
  locations: string[];
  evaluationLog: { messages: GmsLogMessage[] };
  evaluationTimestamp: string;
  results: GmsSortingFractionResult[];
}
export interface GmsEvaluationResponse {
  id: string;
  algorithmVersion: string;
  analyzedObject: string;
  evaluationRequest: string;
  locations: string[];
  referenceTimestamp: string;
  results: GmsResult[];
}

export interface MaterialLayer {
  guid: string;
  functionName: string;
  materialName: string;
  materialManifestationName: string;
  mass: number;
  colorName: string | null;
  index: number;
}
export interface MultiMaterial {
  guid: string;
  totalWeight: number;
  layers: MaterialLayer[];
}
export interface PackagingComponent {
  id: number;
  packagingComponentTypeName: string;
  packagingComponentSubtypeName: string;
  packagingComponentCategoryName: string;
  articleName: string;
  isRigidComponent: boolean | null;
  multiMaterial: MultiMaterial;
}
export interface ComponentEntry {
  guid: string;
  underlyingComponent: PackagingComponent;
}
export interface PackagingUnit {
  id: number;
  guid: string;
  version: number;
  brandName: string;
  productName: string;
  packagingTypeName: string;
  assemblyCountry: string;
  distributionCountries: string[];
  mainBody: ComponentEntry;
  closures: ComponentEntry[];
  labels: ComponentEntry[];
  sleeves: ComponentEntry[];
  inlays: ComponentEntry[];
  inMoldLabels: ComponentEntry[];
  packagingAids: ComponentEntry[];
}
export interface EvaluateRecyclabilityRequest {
  id: number;
  version: number;
  ignoreCustomerAssumptions: boolean;
  evaluationReferenceTimestamp: string;
  evaluationCountries: string[];
}

export interface TranslationDto {
  culture: string;
  translatedName: string;
}
export interface WasteStreamTranslationDto extends TranslationDto {
  location: string;
  translatedDescription: string;
}
export interface WasteStreamDto {
  id: string;
  name: string;
  translations: WasteStreamTranslationDto[];
}

// ----- view models ----------------------------------------------------------

export interface LayerViewModel {
  guid: string;
  functionName: string;
  materialName: string;
  manifestationName: string;
  mass: number;
  color: string | null;
  rating: Rating;
  ratingPercentage: number;
  disclaimers: GmsDisclaimer[];
}
export interface ComponentViewModel {
  guid: string;
  groupName: string;
  typeName: string;
  subtypeName: string;
  articleName: string;
  totalWeight: number;
  isRigid: boolean | null;
  layers: LayerViewModel[];
  aggregateRating: Rating;
  disclaimers: GmsDisclaimer[];
}
export interface SortingFractionViewModel {
  id: string;
  fractionGuid: string;
  name: string;
  componentGuids: string[];
  disclaimers: GmsDisclaimer[];
}

// ============================================================================
// Component
// ============================================================================

@Component({
  selector: 'app-recyclability-result',
  templateUrl: './recyclability-result.component.html',
  styleUrls: ['./recyclability-result.component.scss'],
})
export class RecyclabilityResultComponent {
  // Form fields
  id: number | null = null;
  version: number | null = null;

  // Fixed request body values
  evaluationCountries: string[] = ['DE'];
  evaluationReferenceTimestamp = '2026-06-01T00:00:00+00:00';
  ignoreCustomerAssumptions = false;

  sortingFractionNames: Record<string, string> = {};

  packagingUnit?: PackagingUnit;

  // evaluation as signal so computed logMessages reacts to changes
  readonly evaluationSignal = signal<GmsEvaluationResponse | undefined>(undefined);

  get evaluation(): GmsEvaluationResponse | undefined {
    return this.evaluationSignal();
  }
  set evaluation(val: GmsEvaluationResponse | undefined) {
    this.evaluationSignal.set(val);
  }

  readonly components = signal<ComponentViewModel[]>([]);
  readonly fractions = signal<SortingFractionViewModel[]>([]);
  readonly routingNote = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly loading = signal<boolean>(false);

  // Log
  logExpanded: Record<string, boolean> = {};
  readonly logLevels: GmsLogLevel[] = ['Error', 'Warning', 'Information', 'Debug'];
  private activeLogLevels = new Set<GmsLogLevel>(['Error', 'Warning', 'Information']);

  readonly logMessages = computed<GmsLogMessage[]>(() => (this.evaluationSignal()?.results ?? []).flatMap(r => r.evaluationLog?.messages ?? []));

  filteredLogMessages(): GmsLogMessage[] {
    return this.logMessages().filter(m => this.activeLogLevels.has(m.logLevel));
  }

  isLogLevelActive(level: GmsLogLevel): boolean {
    return this.activeLogLevels.has(level);
  }

  toggleLogLevel(level: GmsLogLevel): void {
    if (this.activeLogLevels.has(level)) {
      this.activeLogLevels.delete(level);
    } else {
      this.activeLogLevels.add(level);
    }
  }

  isLogExpandedForFraction(fractionId: string): boolean {
    return !!this.logExpanded[fractionId];
  }

  toggleLogForFraction(fractionId: string): void {
    this.logExpanded[fractionId] = !this.logExpanded[fractionId];
  }

  logMessagesForFraction(f: SortingFractionViewModel): GmsLogMessage[] {
    const allMessages = (this.evaluationSignal()?.results ?? []).flatMap(r => r.evaluationLog?.messages ?? []);

    // Split into blocks at each "Starting to evaluate Packaging Component Collection" message
    const blocks: GmsLogMessage[][] = [];
    let current: GmsLogMessage[] = [];
    for (const m of allMessages) {
      if (m.content.includes('Starting to evaluate Packaging Component Collection')) {
        if (current.length > 0) blocks.push(current);
        current = [m];
      } else {
        current.push(m);
      }
    }
    if (current.length > 0) blocks.push(current);

    // Assign block by fraction index
    const fractionIndex = this.fractions().findIndex(fr => fr.id === f.id);
    return blocks[fractionIndex] ?? [];
  }

  filteredLogMessagesForFraction(f: SortingFractionViewModel): GmsLogMessage[] {
    return this.logMessagesForFraction(f).filter(m => this.activeLogLevels.has(m.logLevel));
  }

  private readonly severity: Record<Rating, number> = {
    GREEN: 0,
    UNKNOWN: 1,
    YELLOW: 2,
    RED: 3,
  };

  readonly componentsByGuid = computed(() => {
    const m = new Map<string, ComponentViewModel>();
    for (const c of this.components()) m.set(c.guid, c);
    return m;
  });

  readonly ratingTally = computed(() => {
    const t: Record<Rating, number> = { GREEN: 0, YELLOW: 0, RED: 0, UNKNOWN: 0 };
    for (const c of this.components()) for (const l of c.layers) t[l.rating]++;
    return t;
  });

  constructor(
    private readonly http: HttpClient,
    private readonly dialog: MatDialog,
    private readonly translocoService: TranslocoService,
    private readonly analysisApiService: AnalysisApiService
  ) {}

  // --------------------------------------------------------------------------
  // Form action
  // --------------------------------------------------------------------------

  evaluate(): void {
    if (this.id == null || this.version == null) return;
    this.error.set(null);
    this.loading.set(true);
    this.evaluationSignal.set(undefined);
    this.logExpanded = {};
    const id = this.id;
    const version = this.version;

    this.analysisApiService.getRecyclabilityAnalysisProblemInformation(id, PackagingPart.Unit, version).subscribe({
      next: problemInformation => {
        if (problemInformation.length > 0) {
          this.loading.set(false);
          this.dialog.open(AnalysisNotPossibleDialogComponent, {
            data: {
              generalExplanation: this.translocoService.translate('analysisNotPossibleInfo.generalExplanation'),
              problemInformation,
            },
          });
          return;
        }
        this.proceedWithEvaluation(id, version);
      },
      error: () => {
        // if check fails, proceed anyway
        this.proceedWithEvaluation(id, version);
      },
    });
  }

  private proceedWithEvaluation(id: number, version: number): void {
    this.getPackagingUnitById(id, version).subscribe({
      next: unit => {
        this.packagingUnit = unit;
        this.evaluateRecyclability(id, version).subscribe({
          next: evaluation => {
            this.evaluationSignal.set(evaluation);
            this.getWasteStreams().subscribe({
              next: (streams: WasteStreamDto[]) => {
                this.sortingFractionNames = Object.fromEntries(
                  streams.map(s => {
                    const translation = s.translations?.find(t => t.culture === 'de-DE') ?? s.translations?.find(t => t.culture?.startsWith('de')) ?? s.translations?.[0];
                    return [s.id, translation?.translatedName ?? s.name];
                  })
                );
                this.rebuild();
                this.loading.set(false);
              },
              error: () => {
                this.rebuild();
                this.loading.set(false);
              },
            });
          },
          error: () => {
            this.loading.set(false);
            this.error.set('Could not evaluate recyclability.');
          },
        });
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Could not load the packaging unit.');
      },
    });
  }

  // --------------------------------------------------------------------------
  // API requests
  // --------------------------------------------------------------------------

  public getPackagingUnitById(id: number, version: number): Observable<PackagingUnit> {
    const url = `${environment.webApiAddress}/api/packaging-units/by-id?id=${id}&version=${version}`;
    return this.http.get<PackagingUnit>(url);
  }

  public getWasteStreams(): Observable<WasteStreamDto[]> {
    return this.http.get<WasteStreamDto[]>(`${environment.webApiAddress}/api/waste-streams`, {
      headers: { 'Accept-Version': '4.0' },
    });
  }

  public evaluateRecyclability(id: number, version: number): Observable<GmsEvaluationResponse> {
    const url = `${environment.webApiAddress}/api/packaging-units/evaluate-recyclability/gms`;
    const body: EvaluateRecyclabilityRequest = {
      id,
      version,
      ignoreCustomerAssumptions: this.ignoreCustomerAssumptions,
      evaluationReferenceTimestamp: this.evaluationReferenceTimestamp,
      evaluationCountries: this.evaluationCountries,
    };
    return this.http.post<GmsEvaluationResponse[]>(url, body, { headers: { 'Accept-Version': '4.0' } }).pipe(map(arr => arr[0]));
  }

  // --------------------------------------------------------------------------
  // Mapping
  // --------------------------------------------------------------------------

  private rebuild(): void {
    if (!this.packagingUnit || !this.evaluationSignal()) return;
    this.components.set(this.buildComponents());
    this.fractions.set(this.buildSortingFractions());
    this.routingNote.set(this.finalRoutingMessage());
    this.buildGuidNames();
  }

  private collectEntries(): Array<{ group: string; entry: ComponentEntry }> {
    const u = this.packagingUnit!;
    const groups: Array<[string, ComponentEntry[]]> = [
      ['Main Body', u.mainBody ? [u.mainBody] : []],
      ['Closure', u.closures ?? []],
      ['Label', u.labels ?? []],
      ['Sleeve', u.sleeves ?? []],
      ['Inlay', u.inlays ?? []],
      ['In-Mold Label', u.inMoldLabels ?? []],
      ['Packaging Aid', u.packagingAids ?? []],
    ];
    return groups.flatMap(([group, entries]) => entries.map(entry => ({ group, entry })));
  }

  private indexGmsResults(): Map<string, Map<string, GmsLayerResult>> {
    const byComponent = new Map<string, Map<string, GmsLayerResult>>();
    const compResults = (this.evaluationSignal()!.results ?? []).flatMap(r => r.results ?? []).flatMap(f => f.results ?? []);
    for (const comp of compResults) {
      const layerMap = byComponent.get(comp.analyzedObject) ?? new Map<string, GmsLayerResult>();
      for (const layer of comp.results ?? []) layerMap.set(layer.analyzedObject, layer);
      byComponent.set(comp.analyzedObject, layerMap);
    }
    return byComponent;
  }

  private indexComponentDisclaimers(): Map<string, GmsDisclaimer[]> {
    const byComponent = new Map<string, Map<string, GmsDisclaimer>>();
    const compResults = (this.evaluationSignal()!.results ?? []).flatMap(r => r.results ?? []).flatMap(f => f.results ?? []);
    for (const comp of compResults) {
      const existing = byComponent.get(comp.analyzedObject) ?? new Map<string, GmsDisclaimer>();
      for (const d of comp.disclaimers ?? []) existing.set(d.id, d);
      byComponent.set(comp.analyzedObject, existing);
    }
    const result = new Map<string, GmsDisclaimer[]>();
    for (const [guid, map] of byComponent) result.set(guid, [...map.values()]);
    return result;
  }

  private dominantRating(layer?: GmsLayerResult): { rating: Rating; percentage: number } {
    if (!layer?.ratings?.length) return { rating: 'UNKNOWN', percentage: 100 };
    return layer.ratings.reduce((best, cur) => (cur.percentage > best.percentage ? cur : best));
  }

  private aggregate(layers: LayerViewModel[]): Rating {
    if (!layers.length) return 'UNKNOWN';
    return layers.reduce<Rating>((worst, l) => (this.severity[l.rating] > this.severity[worst] ? l.rating : worst), 'GREEN');
  }

  private buildComponents(): ComponentViewModel[] {
    const gmsIndex = this.indexGmsResults();
    const disclaimerIndex = this.indexComponentDisclaimers();
    return this.collectEntries().map(({ group, entry }) => {
      const comp = entry.underlyingComponent;
      const layerResults = gmsIndex.get(entry.guid) ?? new Map<string, GmsLayerResult>();
      const layers: LayerViewModel[] = (comp.multiMaterial?.layers ?? []).map(layer => {
        const layerResult = layerResults.get(layer.guid);
        const { rating, percentage } = this.dominantRating(layerResult);
        const disclaimers = (layerResult?.ratings ?? []).flatMap(r => r.disclaimers ?? []);
        return {
          guid: layer.guid,
          functionName: layer.functionName,
          materialName: layer.materialName,
          manifestationName: layer.materialManifestationName,
          mass: layer.mass,
          color: layer.colorName,
          rating,
          ratingPercentage: percentage,
          disclaimers,
        };
      });
      return {
        guid: entry.guid,
        groupName: group,
        typeName: comp.packagingComponentTypeName,
        subtypeName: comp.packagingComponentSubtypeName,
        articleName: comp.articleName,
        totalWeight: comp.multiMaterial?.totalWeight ?? 0,
        isRigid: comp.isRigidComponent,
        layers,
        aggregateRating: this.aggregate(layers),
        disclaimers: disclaimerIndex.get(entry.guid) ?? [],
      };
    });
  }

  private buildSortingFractions(): SortingFractionViewModel[] {
    return (this.evaluationSignal()!.results ?? [])
      .flatMap(r => r.results ?? [])
      .map(f => ({
        id: f.id,
        fractionGuid: f.sortingFraction,
        name: this.sortingFractionNames[f.sortingFraction] ?? f.sortingFraction,
        componentGuids: (f.results ?? []).map(c => c.analyzedObject),
        disclaimers: f.disclaimers ?? [],
      }));
  }

  private finalRoutingMessage(): string | null {
    const messages = this.evaluationSignal()!.results?.[0]?.evaluationLog?.messages ?? [];
    const info = messages.filter(m => m.logLevel === 'Information');
    return info.length ? info[info.length - 1].content : null;
  }

  // --------------------------------------------------------------------------
  // GUID → name lookup
  // --------------------------------------------------------------------------

  guidNames: Map<string, string> = new Map();

  private buildGuidNames(): void {
    const map = new Map<string, string>();
    if (!this.packagingUnit) return;

    for (const { group, entry } of this.collectEntries()) {
      const comp = entry.underlyingComponent;
      const compLabel = `${group} · ${comp.packagingComponentSubtypeName}${comp.articleName ? ' · ' + comp.articleName : ''}`;
      map.set(entry.guid, compLabel);

      for (const layer of comp.multiMaterial?.layers ?? []) {
        const layerLabel = `${compLabel} → ${layer.materialName}${
          layer.materialManifestationName !== layer.materialName ? ' (' + layer.materialManifestationName + ')' : ''
        } [Layer ${layer.index + 1}]`;
        map.set(layer.guid, layerLabel);
      }
    }

    this.guidNames = map;
  }

  resolveGuids(content: string): Array<{ text: string; isKnown: boolean; isGuid: boolean; name?: string; guid?: string }> {
    const parts: Array<{ text: string; isKnown: boolean; isGuid: boolean; name?: string; guid?: string }> = [];
    const guidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
    let last = 0;
    let match: RegExpExecArray | null;

    while ((match = guidRegex.exec(content)) !== null) {
      if (match.index > last) {
        parts.push({ text: content.slice(last, match.index), isGuid: false, isKnown: false });
      }
      const guid = match[0];
      const name = this.guidNames.get(guid.toLowerCase());
      parts.push({ text: name ?? guid, isGuid: true, isKnown: !!name, guid, name });
      last = match.index + guid.length;
    }

    if (last < content.length) {
      parts.push({ text: content.slice(last), isGuid: false, isKnown: false });
    }

    return parts;
  }

  // --------------------------------------------------------------------------
  // Template helpers
  // --------------------------------------------------------------------------

  componentsForFraction(f: SortingFractionViewModel): ComponentViewModel[] {
    const lookup = this.componentsByGuid();
    return f.componentGuids.map(g => lookup.get(g)).filter((c): c is ComponentViewModel => !!c);
  }

  allDisclaimersForComponent(c: ComponentViewModel): GmsDisclaimer[] {
    const map = new Map<string, GmsDisclaimer>();
    for (const d of c.disclaimers) map.set(d.id, d);
    for (const l of c.layers) for (const d of l.disclaimers) map.set(d.id, d);
    return [...map.values()];
  }

  disclaimerLabel(d: GmsDisclaimer): string {
    const translation = d.translations?.find(t => t.culture === 'de-DE') ?? d.translations?.find(t => t.culture?.startsWith('de')) ?? d.translations?.[0];
    return translation?.translatedName ?? d.name;
  }

  disclaimerTooltip(d: GmsDisclaimer): string {
    const translation = d.translations?.find(t => t.culture === 'de-DE') ?? d.translations?.find(t => t.culture?.startsWith('de')) ?? d.translations?.[0];
    return translation?.translatedDescription ?? d.description ?? d.name;
  }

  ratingLabel(rating: Rating): string {
    switch (rating) {
      case 'GREEN':
        return 'Recyclable';
      case 'YELLOW':
        return 'Limited';
      case 'RED':
        return 'Not recyclable';
      default:
        return 'Unknown';
    }
  }

  trackByGuid(_: number, item: { guid: string }): string {
    return item.guid;
  }
  trackByFraction(_: number, item: SortingFractionViewModel): string {
    return item.id;
  }

  // --------------------------------------------------------------------------
  // Mock
  // --------------------------------------------------------------------------

  testWithMockData(): void {
    this.packagingUnit = {
      id: 9999,
      guid: '2adb27de-96a9-4345-ad3c-c2e0fe5c6f0e',
      version: 1,
      brandName: 'Mock Brand',
      productName: 'Mock Beverage Carton',
      packagingTypeName: 'Carton',
      assemblyCountry: 'DE',
      distributionCountries: ['DE'],
      mainBody: {
        guid: 'c363a806-2551-415d-8c2e-0eab96c01382',
        underlyingComponent: {
          id: 1,
          packagingComponentTypeName: 'Main Body',
          packagingComponentSubtypeName: 'Beverage Carton',
          packagingComponentCategoryName: 'Main Body',
          articleName: 'Carton XYZ',
          isRigidComponent: false,
          multiMaterial: {
            guid: 'mm-1',
            totalWeight: 28.5,
            layers: [
              {
                guid: '13169663-5d51-4fbc-a2d9-854157bdd396',
                functionName: 'Coating / Printing / Ink',
                materialName: 'Ink',
                materialManifestationName: 'Printing ink',
                mass: 0.5,
                colorName: 'dark-colored, opaque',
                index: 0,
              },
              {
                guid: '637dc9dc-ebe4-4fed-a6b4-acbc0d73eab2',
                functionName: 'Material Layer',
                materialName: 'PP',
                materialManifestationName: 'PP',
                mass: 5.5,
                colorName: 'colorless, transparent',
                index: 1,
              },
              {
                guid: '5f520ba5-fd95-41da-adbe-cb47d70a53f1',
                functionName: 'Barrier Layer',
                materialName: 'Aluminium',
                materialManifestationName: 'Aluminium foil',
                mass: 1.5,
                colorName: null,
                index: 2,
              },
              {
                guid: '369e7765-ccb7-4ec8-8933-087863aad623',
                functionName: 'Material Layer',
                materialName: 'Paper',
                materialManifestationName: 'Paperboard',
                mass: 18.0,
                colorName: 'white, opaque',
                index: 3,
              },
              {
                guid: 'c7810062-edf7-4bbd-a421-98a145f31ec7',
                functionName: 'Adhesive',
                materialName: 'Adhesive',
                materialManifestationName: 'Hot melt adhesive',
                mass: 1.5,
                colorName: null,
                index: 4,
              },
              {
                guid: '37246653-ad30-46e1-8d4e-151f0112bed3',
                functionName: 'Material Layer',
                materialName: 'PE',
                materialManifestationName: 'LDPE',
                mass: 1.5,
                colorName: 'colorless, transparent',
                index: 5,
              },
            ],
          },
        },
      },
      closures: [],
      labels: [],
      sleeves: [],
      inlays: [],
      inMoldLabels: [],
      packagingAids: [],
    };

    this.sortingFractionNames = {
      '7ed47258-b11e-4006-a75f-148e21ca3d56': 'Beverage Cartons (Chapter 2.3a)',
    };

    this.evaluationSignal.set({
      id: '059c14b8-8db0-4b04-aeb5-ab57344e55c4',
      algorithmVersion: '0.1',
      analyzedObject: '2adb27de-96a9-4345-ad3c-c2e0fe5c6f0e',
      evaluationRequest: 'a69714dc-faba-4552-b556-be3fa5dc40b4',
      locations: ['17b19475-0841-4723-88c1-e0dc6597766b'],
      referenceTimestamp: '2026-06-30T00:00:00+00:00',
      results: [
        {
          id: '6916d8da-87d9-48ca-98d0-992f225738ad',
          locations: ['17b19475-0841-4723-88c1-e0dc6597766b'],
          evaluationTimestamp: '2026-07-01T05:46:18.2092374+00:00',
          evaluationLog: {
            messages: [
              { content: "Starting to evaluate Packaging Component Collection 'fa66f8ef-69e5-463c-87ba-46e0de95d6a3'...", logLevel: 'Information' },
              { content: 'The input consists of the following packaging components: c363a806-2551-415d-8c2e-0eab96c01382', logLevel: 'Information' },
              { content: "Material Layer '13169663-5d51-4fbc-a2d9-854157bdd396' contributes to the following sorting fractions: 'Residual Waste'", logLevel: 'Debug' },
              {
                content: "Material Layer '637dc9dc-ebe4-4fed-a6b4-acbc0d73eab2' contributes to the following sorting fractions: 'Plastic (Chapters 2.7, 2.8a)'",
                logLevel: 'Debug',
              },
              {
                content: "Material Layer '5f520ba5-fd95-41da-adbe-cb47d70a53f1' contributes to the following sorting fractions: 'Metal (Chapters 2.4, 2.5, 2.6)'",
                logLevel: 'Debug',
              },
              {
                content: "Material Layer '369e7765-ccb7-4ec8-8933-087863aad623' contributes to the following sorting fractions: 'Paper (Chapters 2.2, 2.3a, 2.3b)'",
                logLevel: 'Debug',
              },
              { content: "Material Layer 'c7810062-edf7-4bbd-a421-98a145f31ec7' contributes to the following sorting fractions: 'Residual Waste'", logLevel: 'Debug' },
              {
                content: "Material Layer '37246653-ad30-46e1-8d4e-151f0112bed3' contributes to the following sorting fractions: 'Plastic (Chapters 2.7, 2.8a)'",
                logLevel: 'Debug',
              },
              { content: "The given input will be processed in Sorting Fraction 'Paper (Chapters 2.2, 2.3a, 2.3b)'.", logLevel: 'Information' },
              {
                content:
                  '100,00% of Input \'fa66f8ef-69e5-463c-87ba-46e0de95d6a3\' are compatible with Sorting Fraction 2.3a "Beverage Cartons". The input will be processed in this sorting fraction.',
                logLevel: 'Information',
              },
              { content: "The given input will be processed in Sorting Fraction 'Beverage Cartons (Chapter 2.3a)'.", logLevel: 'Information' },
              {
                content: "Input '13169663-5d51-4fbc-a2d9-854157bdd396' matches Rule 'A2.3.a.38'. The recyclability rating associated with the rule is 'Yellow'.",
                logLevel: 'Debug',
              },
              { content: "Input '637dc9dc-ebe4-4fed-a6b4-acbc0d73eab2' matches Rule 'A2.3.a.2'. The recyclability rating associated with the rule is 'Green'.", logLevel: 'Debug' },
              { content: "Input '5f520ba5-fd95-41da-adbe-cb47d70a53f1' matches Rule 'A2.3.a.5'. The recyclability rating associated with the rule is 'Green'.", logLevel: 'Debug' },
              { content: "Input '369e7765-ccb7-4ec8-8933-087863aad623' matches Rule 'A2.3.a.1'. The recyclability rating associated with the rule is 'Green'.", logLevel: 'Debug' },
              {
                content: "Input 'c7810062-edf7-4bbd-a421-98a145f31ec7' matches Rule 'A2.3.a.47'. The recyclability rating associated with the rule is 'Yellow'.",
                logLevel: 'Debug',
              },
              {
                content: "Input '37246653-ad30-46e1-8d4e-151f0112bed3' matches Rule 'A2.3.a.13'. The recyclability rating associated with the rule is 'Green'.",
                logLevel: 'Debug',
              },
              { content: "Input 'c363a806-2551-415d-8c2e-0eab96c01382' violates Criterion 'BeverageCartonDefaultStructure'!", logLevel: 'Information' },
              {
                content: "Input 'c363a806-2551-415d-8c2e-0eab96c01382' matches Rule 'A2.3.a.58'. The recyclability rating associated with the rule is 'Yellow'.",
                logLevel: 'Debug',
              },
            ],
          },
          results: [
            {
              id: 'fa66f8ef-69e5-463c-87ba-46e0de95d6a3',
              sortingFraction: '7ed47258-b11e-4006-a75f-148e21ca3d56',
              disclaimers: [
                {
                  id: 'd-frac-1',
                  name: 'F1',
                  description: 'This sorting fraction assumes standard collection infrastructure availability.',
                  translations: [{ culture: 'de-DE', translatedName: 'F1', translatedDescription: 'Diese Sortierfraktion setzt eine Standard-Sammelinfrastruktur voraus.' }],
                },
              ],
              results: [
                {
                  id: 'e6dc92df-31e6-4210-ada2-a65b4b1ee3dd',
                  analyzedObject: 'c363a806-2551-415d-8c2e-0eab96c01382',
                  disclaimers: [
                    {
                      id: '99d61637-6976-4a3e-9a23-81f62f50aa2a',
                      name: 'P5',
                      description: 'Adhesive type is not verified against approved list.',
                      translations: [{ culture: 'de-DE', translatedName: 'P5', translatedDescription: 'Der Klebstofftyp ist nicht gegen die zugelassene Liste geprüft.' }],
                    },
                    {
                      id: '3be1b3b3-a0a0-41fc-8983-3d2fd57a100b',
                      name: 'P2',
                      description: 'Printing coverage exceeds recommended threshold.',
                      translations: [{ culture: 'de-DE', translatedName: 'P2', translatedDescription: 'Die Druckabdeckung überschreitet den empfohlenen Grenzwert.' }],
                    },
                  ],
                  results: [
                    {
                      id: '804313dc-6756-4feb-a724-e90a4274cb92',
                      analyzedObject: '13169663-5d51-4fbc-a2d9-854157bdd396',
                      ratings: [
                        {
                          rating: 'YELLOW',
                          percentage: 100,
                          disclaimers: [
                            {
                              id: 'd-1',
                              name: 'P2',
                              description: 'Printing ink may affect recyclability if coverage exceeds 50%.',
                              translations: [
                                {
                                  culture: 'de-DE',
                                  translatedName: 'P2',
                                  translatedDescription: 'Druckfarbe kann die Recyclingfähigkeit beeinträchtigen, wenn die Abdeckung 50% übersteigt.',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      id: '5053ad8e-d1fc-415b-9e24-62f0320b46f7',
                      analyzedObject: '637dc9dc-ebe4-4fed-a6b4-acbc0d73eab2',
                      ratings: [{ rating: 'GREEN', percentage: 100, disclaimers: [] }],
                    },
                    {
                      id: '1ec25962-35b0-4f4a-8d28-64ab51d0ee2e',
                      analyzedObject: '5f520ba5-fd95-41da-adbe-cb47d70a53f1',
                      ratings: [{ rating: 'GREEN', percentage: 100, disclaimers: [] }],
                    },
                    {
                      id: 'db385f65-9ea0-45d6-b593-1eceb6a19f4b',
                      analyzedObject: '369e7765-ccb7-4ec8-8933-087863aad623',
                      ratings: [{ rating: 'GREEN', percentage: 100, disclaimers: [] }],
                    },
                    {
                      id: '16a78fc5-c34c-4d59-8d95-fd4e7a07b95d',
                      analyzedObject: 'c7810062-edf7-4bbd-a421-98a145f31ec7',
                      ratings: [
                        {
                          rating: 'YELLOW',
                          percentage: 100,
                          disclaimers: [
                            {
                              id: 'd-2',
                              name: 'P5',
                              description: 'Adhesive type is not verified against approved list.',
                              translations: [{ culture: 'de-DE', translatedName: 'P5', translatedDescription: 'Der Klebstofftyp ist nicht gegen die zugelassene Liste geprüft.' }],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      id: '205750de-28e2-4e13-8d5a-40fc69522369',
                      analyzedObject: '37246653-ad30-46e1-8d4e-151f0112bed3',
                      ratings: [{ rating: 'GREEN', percentage: 100, disclaimers: [] }],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    });

    this.rebuild();
  }
}
